import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { find, isArray, isNumber } from 'lodash';
import { Map, TileLayer } from 'react-leaflet';

import GeoscatterMapItem from './geoscatter-map-item';
import GeoheatMapItem from './geoheatmap-map-item';
import GeochoroplethItem from './geochoropleth-map-item';

import MAP_ROLES from 'config/map-types';
import { ThemeContext } from 'constants/theme';
import {
  getTileURL,
  MAX_LATLNG_BOUNDS,
  ZOOM_MAX,
  ZOOM_MIN,
} from 'constants/geospatial';
import GeoSpatialHeatmapRole from 'config/map-types/geospatial-heatmap';
import GeoSpatialScatterRole from 'config/map-types/geospatial-scatter';
import GeoSpatialChoroplethRole from 'config/map-types/geospatial-choropleth';

import { getHereAttributionMessage } from 'utils/map';

class MapItem extends PureComponent {
  static propTypes = {
    _id: PropTypes.string,
    data: PropTypes.shape({
      documents: PropTypes.array,
      metadata: PropTypes.object,
    }).isRequired,
    spec: PropTypes.object.isRequired,
    chartType: PropTypes.string,
    className: PropTypes.string,
    onViewportChanged: PropTypes.func,
    isResizing: PropTypes.bool,
    width: PropTypes.number,
    height: PropTypes.number,
    tileServer: PropTypes.string.isRequired,
    tileAttributionMessage: PropTypes.string.isRequired,
  };

  static defaultProps = {
    isResizing: false,
  };

  state = {
    ready: false,
    attributionMessage: '',
  };

  componentDidMount() {
    const { map } = this.refs;

    if (map) {
      // Called on zoom and pan
      map.leafletElement.on('moveend', this.onViewportChanged);
      const {
        spec: { viewport },
      } = this.props;

      if (viewport && !this.props.spec.autoViewport) {
        map.leafletElement.setView(viewport.center, viewport.zoom);
      }
    }
  }

  componentDidUpdate(prevProps) {
    this.invalidateMapSize();

    // in case of resize
    if (
      (prevProps.width !== this.props.width ||
        prevProps.height !== this.props.height) &&
      this.props.spec.autoViewport
    ) {
      const { map } = this.refs;

      if (map) {
        map.leafletElement.fitBounds(this.props.spec.bounds);
      }
    }
  }

  onViewportChanged = () => {
    if (!this.state.ready) {
      return;
    }

    const { map } = this.refs;

    if (!this.props.onViewportChanged || !map) {
      return;
    }

    const { viewport } = map;

    if (isArray(viewport.center) && isNumber(viewport.zoom)) {
      this.props.onViewportChanged(viewport.center, viewport.zoom);
    }
  };

  onReady = () => {
    if (this.state.ready) {
      return;
    }

    this.getTileAttribution();
    this.setState({ ready: true }, this.invalidateMapSize);
  };

  getTileAttribution = async () => {
    const { tileServer, tileAttributionMessage } = this.props;
    const { map } = this.refs;

    let attributionMessage = tileAttributionMessage;
    if (!tileServer && map) {
      const bounds = map.leafletElement.getBounds();
      const level = map.leafletElement.getZoom();

      attributionMessage = await getHereAttributionMessage(bounds, level);
    }
    this.setState({ attributionMessage });
  };

  invalidateMapSize() {
    const { map } = this.refs;

    if (map) {
      map.leafletElement.invalidateSize();
    }
  }

  renderMapItem() {
    const {
      _id,
      chartType,
      data: { documents, metadata },
      spec,
    } = this.props;

    const mapRole = find(MAP_ROLES, { name: chartType });

    if (mapRole === GeoSpatialChoroplethRole) {
      return <GeochoroplethItem {...metadata} {...spec} />;
    }

    const geopoints = documents.map(doc => doc.geopoint);

    switch (mapRole) {
      case GeoSpatialScatterRole:
        return <GeoscatterMapItem data={geopoints} />;
      case GeoSpatialHeatmapRole:
        return <GeoheatMapItem {...spec} _id={_id} data={geopoints} />;
      default:
        return null;
    }
  }

  render() {
    const { attributionMessage } = this.state;
    const { spec, tileServer } = this.props;

    // eslint-disable-next-line no-unused-vars
    const { viewport, ...mapProps } = spec;

    return (
      <Map
        {...mapProps}
        ref="map"
        preferCanvas
        minZoom={ZOOM_MIN}
        maxZoom={ZOOM_MAX}
        maxBounds={MAX_LATLNG_BOUNDS}
        whenReady={this.onReady}
        onMoveend={this.getTileAttribution}
      >
        {this.renderMapItem()}
        <ThemeContext.Consumer>
          {theme => (
            <TileLayer
              url={tileServer || getTileURL(theme)}
              attribution={attributionMessage}
            />
          )}
        </ThemeContext.Consumer>
      </Map>
    );
  }
}

export default MapItem;
export { MapItem };
